<script setup>
import { storeToRefs } from 'pinia';
import { nextTick } from 'vue';
import { useBiChartBuilderHelpers } from '~/bi/composables/bi-chart-builder-helpers.composable';
import { BI_ACTUAL_CHARTS, BI_DEFAULT_PALETTE_COLORS, SERIES_SUPPORTED_CHARTS } from '~/bi/constants/bi-constants';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';

const props = defineProps({
  chartType: {
    type: String,
    required: true,
  },
  chartConfig: {
    type: Object,
    required: true,
  },
  formInstance: {
    type: Object,
    required: true,
  },
  mapColumns: {
    type: Array,
  },
});

const emit = defineEmits(['configChange']);

const { $t } = useCommonImports();

const { getMarkerValueOptions, min_value_for_gauge_and_progress, max_value_for_gauge_and_progress } = useBiChartBuilderHelpers();

const bi_store = useBiStore();
const { chart_builder_columns_data_types } = storeToRefs(bi_store);

const show_legend = computed(() => !['gauge_chart', 'progress_chart', 'waterfall_chart'].includes(props.chartType));

const line_styles = computed(() => {
  return [
    { uid: 'solid', leftSlot: h('div', { style: 'height: 20px; width: 20px; display: flex; align-items: center; justify-content: center;' }, [h('hr', { style: 'border: none; height: 2px; background-color: currentColor; margin: 0; width: 100%;' })]), tooltip_text: $t('Solid') },
    { uid: 'dashed', leftSlot: IconHawkStroke, tooltip_text: $t('Dashed') },
    { uid: 'dotted', leftSlot: IconHawkDotsHorizontal, tooltip_text: $t('Dotted') },
  ];
});

const state = reactive({
  eighty_percent_line_color: null,
  eighty_percent_line_style: null,
  cumulative_line_color: null,
  bar_color: null,
  positive_color: null,
  negative_color: null,
  number_widget_foreground_color: null,
  number_widget_background_color: null,
});

function onFieldSelected(field, value) {
  state[field] = value;
  props.formInstance.update({
    [field]: value,
  });
}

function onColorRangeItemChange(event, index) {
  emit('configChange', event, index, 'color_ranges');
}

async function onAddColorRange(index) {
  await nextTick();
  emit('configChange', {
    color: BI_DEFAULT_PALETTE_COLORS[Math.floor(Math.random() * BI_DEFAULT_PALETTE_COLORS.length)],
    label: `${$t('Range')} ${index + 1}`,
    value: null,
  }, index, 'color_ranges');
}

watch(() => props.chartConfig, () => {
  ['eighty_percent_line_color', 'eighty_percent_line_style', 'cumulative_line_color', 'bar_color', 'positive_color', 'negative_color', 'number_widget_foreground_color', 'number_widget_background_color'].forEach((key) => {
    if (!props.chartConfig[key]) {
      if (key === 'number_widget_foreground_color') {
        state[key] = '#000000';
      }
      else if (key === 'number_widget_background_color') {
        state[key] = '#FFFFFF';
      }
      else {
        state[key] = props.chartConfig[key];
      }
      return;
    }
    state[key] = props.chartConfig[key];
  });
});
</script>

<template>
  <template v-if="BI_ACTUAL_CHARTS.includes(props.chartType)">
    <TextElement
      name="title"
      :label="$t('Title')"
      :placeholder="$t('Enter title')"
      :description="$t('Main chart title displayed at the top')"
    />
    <TextElement
      name="subtitle"
      :label="$t('Subtitle')"
      :placeholder="$t('Enter subtitle')"
      :description="$t('Secondary title below the main title')"
    />
    <TextElement
      v-if="['gauge_chart', 'progress_chart'].includes(props.chartType)"
      :key="min_value_for_gauge_and_progress"
      name="minimum"
      :label="$t('Minimum')"
      input-type="number"
      :placeholder="min_value_for_gauge_and_progress"
    />
    <TextElement
      v-if="['gauge_chart', 'progress_chart'].includes(props.chartType)"
      :key="max_value_for_gauge_and_progress"
      name="maximum"
      :label="$t('Maximum')"
      input-type="number"
      :placeholder="max_value_for_gauge_and_progress"
    />
    <SelectElement
      v-if="show_legend"
      name="legend"
      :label="$t('Legend')"
      :items="{
        bottom: $t('Bottom'),
        right: $t('Right'),
        top: $t('Top'),
        left: $t('Left'),
        topleft: $t('Top-left'),
        topright: $t('Top-right'),
        bottomleft: $t('Bottom-left'),
        bottomright: $t('Bottom-right'),
        hide: $t('Hide'),
      }"
      :default="props.chartType === 'heatmap_chart' ? 'bottom' : 'topleft'"
      :native="false"
      :can-clear="false"
      :can-deselect="false"
    />
    <ToggleElement
      name="timeseries"
      :label="$t('Timeseries')"
      :conditions="[
        function () {
          return ['column_chart', 'line_chart', 'area_chart', 'mixed_chart'].includes(props.chartType) && props.chartConfig.layout_category && chart_builder_columns_data_types[props.chartConfig.layout_category] === 'date';
        },
      ]"
      :columns="{
        default: { container: 11, label: 11, wrapper: 11 },
        sm: { container: 11, label: 11, wrapper: 11 },
      }"
      align="right"
    />
    <SelectElement
      name="timeseries_mode"
      :label="$t('Mode')"
      :items="{
        both: $t('Slider + Scroll'),
        slider: $t('Slider'),
        inside: $t('Scroll'),
        none: $t('None'),
      }"
      :conditions="[['timeseries', true]]"
      default="inside"
      :native="false"
      :can-clear="false"
      :can-deselect="false"
    />
    <GroupElement
      name="prefix_suffix"
      v-bind="{
        ...(SERIES_SUPPORTED_CHARTS.includes(props.chartType) ? { description: $t('Series specific prefix/suffix will override these values if set') } : {}),
      }"
    >
      <div class="col-span-12 flex gap-3">
        <TextElement
          name="prefix"
          :label="$t('Prefix')"
          :placeholder="$t('Enter prefix')"
          class="w-full"
        />
        <TextElement
          name="suffix"
          :label="$t('Suffix')"
          :placeholder="$t('Enter suffix')"
          class="w-full"
        />
      </div>
    </GroupElement>
    <SelectElement
      name="values"
      :label="$t('Values')"
      :items="{
        show: $t('Show'),
        hide: $t('Hide'),
      }"
      default="show"
      :native="false"
      :can-clear="false"
      :can-deselect="false"
    />
    <SliderElement
      name="precision"
      :label="$t('Precision')"
      :min="0"
      :max="10"
      :default="2"
      :step="1"
      :conditions="[['values', 'show']]"
    />
    <div>
      <ToggleElement
        name="compact"
        :label="$t('Compact')"
        :conditions="[['values', 'show']]"
        :columns="{
          default: { container: 11, label: 11, wrapper: 11 },
          sm: { container: 11, label: 11, wrapper: 11 },
        }"
        align="right"
      />
      <div v-if="props.chartConfig.values === 'show'" class="form-color-muted form-text-small-sm">
        {{ $t('Show values in compact format') }}
      </div>
    </div>
    <template v-if="['waterfall_chart'].includes(props.chartType)">
      <div class="flex justify-between">
        <div class="col-span-4 text-sm font-medium text-gray-700">
          {{ $t('Positive color') }}
        </div>
        <div class="col-span-8">
          <BiColorPicker
            type="outlined"
            :active-color="state.positive_color"
            @color-selected="onFieldSelected('positive_color', $event)"
          />
        </div>
      </div>
      <div class="flex justify-between">
        <div class="col-span-4 text-sm font-medium text-gray-700">
          {{ $t('Negative color') }}
        </div>
        <div class="col-span-8">
          <BiColorPicker
            type="outlined"
            :active-color="state.negative_color"
            @color-selected="onFieldSelected('negative_color', $event)"
          />
        </div>
      </div>
      <ToggleElement
        :label="$t('Show totals')"
        name="show_sum"
        :columns="{
          default: { container: 11, label: 11, wrapper: 11 },
          sm: { container: 11, label: 11, wrapper: 11 },
        }"
        align="right"
      />
      <HiddenElement name="positive_color" />
      <HiddenElement name="negative_color" />
    </template>
    <template v-if="['pareto_chart'].includes(props.chartType)">
      <div class="flex justify-between">
        <div class="col-span-4 text-sm font-medium text-gray-700">
          {{ $t('Bar color') }}
        </div>
        <div class="col-span-8">
          <BiColorPicker
            type="outlined"
            :active-color="state.bar_color"
            @color-selected="onFieldSelected('bar_color', $event)"
          />
        </div>
      </div>
      <div class="flex justify-between">
        <div class="col-span-4 text-sm font-medium text-gray-700">
          {{ $t('Cumulative line color') }}
        </div>
        <div class="col-span-8">
          <BiColorPicker
            type="outlined"
            :active-color="state.cumulative_line_color"
            @color-selected="onFieldSelected('cumulative_line_color', $event)"
          />
        </div>
      </div>
      <ToggleElement
        :label="$t('80% line')"
        name="show_eighty_percent_line"
        :columns="{
          default: { container: 11, label: 11, wrapper: 11 },
          sm: { container: 11, label: 11, wrapper: 11 },
        }"
        align="right"
      />
      <div v-if="props.chartConfig.show_eighty_percent_line" class="flex justify-between">
        <div class="col-span-4 text-sm font-medium text-gray-700">
          &mdash; {{ $t('color') }}
        </div>
        <div class="col-span-8">
          <BiColorPicker
            type="outlined"
            :active-color="state.eighty_percent_line_color"
            @color-selected="onFieldSelected('eighty_percent_line_color', $event)"
          />
        </div>
      </div>
      <div v-if="props.chartConfig.show_eighty_percent_line" class="flex justify-between">
        <div class="col-span-4 text-sm font-medium text-gray-700">
          &mdash; {{ $t('style') }}
        </div>
        <div class="col-span-8">
          <HawkButtonGroup
            :items="line_styles"
            icon
            size="sm"
            :active_item="state.eighty_percent_line_style"
            class="w-fit"
            @select="onFieldSelected('eighty_percent_line_style', $event.uid)"
          />
        </div>
      </div>
      <HiddenElement name="eighty_percent_line_color" />
      <HiddenElement name="eighty_percent_line_style" />
      <HiddenElement name="cumulative_line_color" />
      <HiddenElement name="bar_color" />
    </template>
    <template v-if="['pyramid_chart', 'funnel_chart'].includes(props.chartType)">
      <ToggleElement
        name="show_labels_at_center"
        :label="$t('Show labels at center')"
        :columns="{
          default: { container: 11, label: 11, wrapper: 11 },
          sm: { container: 11, label: 11, wrapper: 11 },
        }"
        align="right"
      />
      <ToggleElement
        name="show_percentages"
        :label="$t('Show percentages')"
        :conditions="[['values', '==', 'show']]"
        :columns="{
          default: { container: 11, label: 11, wrapper: 11 },
          sm: { container: 11, label: 11, wrapper: 11 },
        }"
        align="right"
      />
      <ToggleElement
        v-if="['funnel_chart'].includes(props.chartType)"
        name="compare_with_previous"
        :label="$t('Compare with previous')"
        :conditions="[['show_percentages', '==', true]]"
        :columns="{
          default: { container: 11, label: 11, wrapper: 11 },
          sm: { container: 11, label: 11, wrapper: 11 },
        }"
        align="right"
      />
      <ToggleElement
        v-if="['funnel_chart'].includes(props.chartType)"
        name="maintain_slope"
        :label="$t('Maintain slope')"
        :columns="{
          default: { container: 11, label: 11, wrapper: 11 },
          sm: { container: 11, label: 11, wrapper: 11 },
        }"
        align="right"
      />
      <ToggleElement
        name="render_in_3d"
        :label="$t('Render in 3D')"
        :columns="{
          default: { container: 11, label: 11, wrapper: 11 },
          sm: { container: 11, label: 11, wrapper: 11 },
        }"
        align="right"
      />
      <ToggleElement
        name="outline"
        :label="$t('Outline')"
        :conditions="[['render_in_3d', '==', false]]"
        :columns="{
          default: { container: 11, label: 11, wrapper: 11 },
          sm: { container: 11, label: 11, wrapper: 11 },
        }"
        align="right"
      />
    </template>
    <template v-if="['heatmap_chart'].includes(props.chartType)">
      <SelectElement
        name="color_type"
        :label="$t('Color scheme')"
        :items="{
          piecewise: $t('Piecewise'),
          continuous: $t('Gradient'),
        }"
        default="continuous"
        :native="false"
        :can-clear="false"
        :can-deselect="false"
      />
      <BiColorPalettePicker
        v-if="props.chartConfig.color_type === 'continuous'"
        name="color_scheme"
        variant="heatmap"
        :options="{ label: $t('Gradient') }"
      />
    </template>
    <template v-if="['gauge_chart', 'progress_chart'].includes(props.chartType) || (['heatmap_chart'].includes(props.chartType) && props.chartConfig.color_type === 'piecewise')">
      <ListElement
        name="color_ranges"
        :label="$t('Color ranges')"
        :add-text="`+ ${$t('Add range')}`"
        :controls="{ add: true, remove: true, sort: true }"
        :add-classes="{
          ListElement: {
            handle: 'left-8 top-[1px] visible opacity-100 !z-0',
            add: '!bg-white !border-white !text-primary-700 hover:!scale-100 !mt-0 -ml-1',
            remove: '!-right-4',
          },
        }"
        :remove-classes="{
          ListElement: {
            remove: 'left-0',
          },
        }"
        :columns="{
          default: { container: 12, label: 12, wrapper: 12 },
          sm: { container: 12, label: 12, wrapper: 12 },
          md: { container: 12, label: 12, wrapper: 12 },
        }"
        :initial="0"
        @add="onAddColorRange"
      >
        <template #default="{ index }">
          <ObjectElement :name="index" class="border rounded px-3 pt-3 pb-1">
            <TextElement
              v-if="props.chartType === 'heatmap_chart'"
              name="label"
              @change="onColorRangeItemChange({ label: $event }, index)"
            >
              <template #addon-before>
                <BiColorPicker
                  type="plain"
                  :active-color="props.chartConfig?.color_ranges?.[index]?.color"
                  @color-selected="onColorRangeItemChange({ color: $event }, index)"
                />
              </template>
              <template #addon-after>
                <div class="flex items-center gap-1">
                  <IconHawkXClose
                    class="w-4 h-4 cursor-pointer"
                    @click="props.formInstance.elements$.color_ranges.remove(index)"
                  />
                </div>
              </template>
            </TextElement>
            <div v-else class="col-span-12 flex gap-6">
              <div class="text-sm font-medium text-gray-700">
                {{ $t('Range color') }}
              </div>
              <div>
                <BiColorPicker
                  type="outlined"
                  :active-color="props.chartConfig?.color_ranges?.[index]?.color"
                  @color-selected="onColorRangeItemChange({ color: $event }, index)"
                />
              </div>
            </div>
            <div class="col-span-12 flex gap-3">
              <SelectElement
                name="min"
                :label="$t('Min')"
                :items="getMarkerValueOptions(props.chartConfig?.color_ranges?.[index]?.min)"
                :create="true"
                :native="false"
                :can-clear="false"
                :can-deselect="false"
                :append-new-option="false"
                class="w-full"
                @change="onColorRangeItemChange({ min: $event }, index)"
              >
                <template #after>
                  <div v-if="!getMarkerValueOptions(props.chartConfig?.color_ranges?.[index]?.min).includes(props.chartConfig?.color_ranges?.[index]?.min) && Number.isNaN(Number.parseInt(props.chartConfig?.color_ranges?.[index]?.min))" class="text-error-600 text-sm mt-1">
                    {{ $t('Please enter a valid value') }}
                  </div>
                </template>
              </SelectElement>
              <SelectElement
                name="max"
                :label="$t('Max')"
                :items="getMarkerValueOptions(props.chartConfig?.color_ranges?.[index]?.max)"
                :create="true"
                :native="false"
                :can-clear="false"
                :can-deselect="false"
                :append-new-option="false"
                class="w-full"
                @change="onColorRangeItemChange({ max: $event }, index)"
              >
                <template #after>
                  <div v-if="!getMarkerValueOptions(props.chartConfig?.color_ranges?.[index]?.max).includes(props.chartConfig?.color_ranges?.[index]?.max) && Number.isNaN(Number.parseInt(props.chartConfig?.color_ranges?.[index]?.max))" class="text-error-600 text-sm mt-1">
                    {{ $t('Please enter a valid value') }}
                  </div>
                </template>
              </SelectElement>
            </div>
            <HiddenElement name="color" class="h-0" />
          </ObjectElement>
        </template>
      </ListElement>
    </template>
  </template>
  <template v-else-if="['number_chart'].includes(props.chartType)">
    <SelectElement
      name="compare_by"
      :label="$t('Compare by')"
      :items="{
        none: $t('None'),
        percentage: $t('Percentage change'),
        difference: $t('Value change'),
      }"
      default="percentage"
      :native="false"
      :can-clear="false"
      :can-deselect="false"
    />
    <ToggleElement
      v-if="props.chartConfig.compare_by !== 'none'"
      name="switch_positive_negative_colors"
      :label="$t('Switch positive and negative colors?')"
      :columns="{
        default: { container: 11, label: 11, wrapper: 11 },
        sm: { container: 11, label: 11, wrapper: 11 },
      }"
      align="right"
    />
    <div>
      <ToggleElement
        name="compact"
        :label="$t('Compact')"
        :columns="{
          default: { container: 11, label: 11, wrapper: 11 },
          sm: { container: 11, label: 11, wrapper: 11 },
        }"
        align="right"
      />
      <div class="form-color-muted form-text-small-sm">
        {{ $t('Show values in compact format') }}
      </div>
    </div>
    <div class="flex gap-3">
      <TextElement
        name="prefix"
        :label="$t('Prefix')"
        :placeholder="$t('Enter prefix')"
        class="w-full"
      />
      <TextElement
        name="suffix"
        :label="$t('Suffix')"
        :placeholder="$t('Enter suffix')"
        class="w-full"
      />
    </div>
    <div class="flex justify-between">
      <div class="col-span-4 text-sm font-medium text-gray-700">
        {{ $t('Foreground color') }}
      </div>
      <div class="col-span-8">
        <BiColorPicker
          type="outlined"
          :active-color="state.number_widget_foreground_color"
          :append-white-to-the-start="true"
          @color-selected="onFieldSelected('number_widget_foreground_color', $event)"
        />
      </div>
    </div>
    <div class="flex justify-between">
      <div class="col-span-4 text-sm font-medium text-gray-700">
        {{ $t('Background color') }}
      </div>
      <div class="col-span-8">
        <BiColorPicker
          type="outlined"
          :active-color="state.number_widget_background_color"
          :append-white-to-the-start="true"
          @color-selected="onFieldSelected('number_widget_background_color', $event)"
        />
      </div>
    </div>
    <HiddenElement name="number_widget_foreground_color" />
    <HiddenElement name="number_widget_background_color" />
  </template>
  <template v-else-if="props.chartType === 'map'">
    <BiMapDisplayTab :form-instance="formInstance" :map-columns="mapColumns" />
  </template>
</template>
